import React, { useState } from 'react';
import { COLORS, SHADOWS } from '../../constants/theme';

/**
 * Professionel tooltip til CEO-niveau forklaringer
 * Viser detaljerede danske beskrivelser af alle beregninger
 */
const Tooltip = ({ 
  children, 
  content,
  calculations = [],
  position = 'top',
  trigger = 'click',
  width = '320px'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const handleShow = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipCoords = {
      top: position === 'top' ? rect.top - 10 : rect.bottom + 10,
      left: rect.left + (rect.width / 2),
    };
    setCoords(tooltipCoords);
    setIsVisible(true);
  };

  const handleHide = () => {
    setIsVisible(false);
  };

  const triggerProps = trigger === 'click' 
    ? {
        onClick: (e) => {
          e.stopPropagation();
          isVisible ? handleHide() : handleShow(e);
        }
      }
    : {
        onMouseEnter: handleShow,
        onMouseLeave: handleHide,
      };

  return (
    <>
      <div style={{ position: 'relative', display: 'inline-block' }} {...triggerProps}>
        {children}
        <span style={{
          position: 'absolute',
          top: '2px',
          right: '2px',
          fontSize: '0.6rem',
          color: COLORS.primary.green,
          opacity: 0.5,
          cursor: 'pointer',
          userSelect: 'none'
        }}>
          ⓘ
        </span>
      </div>
      
      {isVisible && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={handleHide}
          />
          
          <div style={{
            position: 'fixed',
            top: position === 'top' 
              ? `${coords.top}px`
              : `${coords.top}px`,
            left: `${coords.left}px`,
            transform: 'translateX(-50%)',
            background: COLORS.ui.surface,
            border: `1px solid ${COLORS.ui.border}`,
            borderRadius: '0.5rem',
            padding: '0.75rem',
            boxShadow: SHADOWS.xl,
            zIndex: 1000,
            width,
            maxWidth: '90vw',
            maxHeight: '400px',
            overflowY: 'auto',
            animation: 'fadeIn 0.2s ease',
          }}>
            <div style={{
              position: 'absolute',
              top: position === 'top' ? '100%' : '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              [position === 'top' ? 'borderTop' : 'borderBottom']: `8px solid ${COLORS.ui.surface}`,
            }} />
            
            <div style={{ 
              fontSize: '0.75rem',
              color: COLORS.ui.text.primary,
              lineHeight: 1.5,
            }}>
              {content && (
                <div style={{ marginBottom: calculations.length > 0 ? '0.5rem' : 0 }}>
                  {content}
                </div>
              )}
              
              {calculations.length > 0 && (
                <div style={{
                  borderTop: `1px solid ${COLORS.ui.border}`,
                  paddingTop: '0.5rem',
                }}>
                  <div style={{
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    color: COLORS.primary.green,
                    marginBottom: '0.25rem',
                  }}>
                    Beregning:
                  </div>
                  
                  {calculations.map((calc, idx) => (
                    <div key={idx} style={{
                      fontSize: '0.65rem',
                      color: COLORS.ui.text.secondary,
                      marginBottom: '0.25rem',
                      paddingLeft: '0.5rem',
                      borderLeft: `2px solid ${COLORS.primary.greenLight}20`,
                    }}>
                      {calc.label && (
                        <span style={{ fontWeight: '500' }}>
                          {calc.label}:
                        </span>
                      )}
                      {' '}
                      {calc.formula && (
                        <span style={{ 
                          fontFamily: 'monospace',
                          background: COLORS.ui.background,
                          padding: '0.125rem 0.25rem',
                          borderRadius: '0.125rem',
                          color: COLORS.primary.green,
                        }}>
                          {calc.formula}
                        </span>
                      )}
                      {calc.value && (
                        <span style={{
                          fontWeight: '600',
                          color: COLORS.ui.text.primary,
                          marginLeft: '0.25rem',
                        }}>
                          = {calc.value}
                        </span>
                      )}
                      {calc.description && (
                        <div style={{
                          marginTop: '0.125rem',
                          color: COLORS.ui.text.muted,
                          fontStyle: 'italic',
                        }}>
                          {calc.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <button
              onClick={handleHide}
              style={{
                position: 'absolute',
                top: '0.5rem',
                right: '0.5rem',
                background: 'transparent',
                border: 'none',
                fontSize: '1rem',
                color: COLORS.ui.text.muted,
                cursor: 'pointer',
                padding: '0.25rem',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        </>
      )}
      
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-5px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </>
  );
};

export default Tooltip;